/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getBackOrderListAsync } from './DataActionExtension.g';
import { IGetBackOrderListResponse } from './DataServiceEntities.g';
import { QueryResultSettingsProxy, buildCacheKey /*generateCacheKey*/ } from '@msdyn365-commerce-modules/retail-actions';

/**
 * GetBackorderList Input Action
 */
export class GetBackorderListInput implements Msdyn365.IActionInput {
    public apiSettings: Msdyn365.ICommerceApiSettings;
    public accountNum: string;
    public top: number;
    public skip: number;
    public selectedSort: string;
    public sortOrder: string;
    public splitGetBackordersCall: boolean;

    constructor(
        context: Msdyn365.IRequestContext,
        apiSettings: Msdyn365.ICommerceApiSettings,
        top: number,
        skip: number,
        selectedSort: string,
        sortOrder: string,
        splitGetBackordersCall: boolean
    ) {
        this.apiSettings = apiSettings;
        // this.accountNum = accountNum;
        this.accountNum = Msdyn365.getCustomerAccountNumberForAllChannelType(context) || '';
        this.top = top;
        this.skip = skip;
        this.selectedSort = selectedSort;
        this.sortOrder = sortOrder;
        this.splitGetBackordersCall = splitGetBackordersCall;
        // console.log(`selectedSort: ${selectedSort}`);
        // console.log(`sortOrder: ${sortOrder}`);
    }

    public getCacheKey = () => {
        const baseCacheKey = 'BackorderList';
        if (this.top && this.skip) {
            // return generateCacheKey(
            //     this.apiSettings,
            //     { key: 'BaseCacheKey', value: baseCacheKey },
            //     { key: 'CustomerAccountNumber', value: this.accountNum },
            //     { key: 'Top', value: this.top },
            //     { key: 'Skip', value: this.skip },
            //     { key: 'SelectedSort', value: this.selectedSort },
            //     { key: 'SortOrder', value: this.sortOrder }
            // );
            return buildCacheKey(
                `${baseCacheKey}-${this.top}-${this.skip}-${this.selectedSort}-${this.sortOrder}-${this.splitGetBackordersCall}`,
                this.apiSettings
            );
        } else {
            // return generateCacheKey(
            //     this.apiSettings,
            //     { key: 'BaseCacheKey', value: baseCacheKey },
            //     { key: 'CustomerAccountNumber', value: this.accountNum }
            // );
            return buildCacheKey(`${baseCacheKey}-${this.selectedSort}-${this.sortOrder}-${this.splitGetBackordersCall}`, this.apiSettings);
        }
    };
    public getCacheObjectType = () => 'BackorderList';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

export interface IGetBackorderListData {
    backOrderData: IGetBackOrderListResponse;
}

const createBackorderListInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const queryResultSettings = QueryResultSettingsProxy.fromInputData(args).QueryResultSettings;
    const apiSettings = args.requestContext.apiSettings;
    const context = args.requestContext;
    // @ts-expect-error
    const splitGetBackordersCall = args.config?.splitGetBackordersCall || false;
    // @ts-expect-error
    const top = args.config?.backOrderItemsPerPage || 15;
    const skip = queryResultSettings.Paging?.Skip || 0;
    const selectedSort = args.requestContext.query?.selectedSort || 'Order Date';
    const sortOrder = args.requestContext.query?.sortOrder || 'Ascending';
    return new GetBackorderListInput(context, apiSettings, top, skip, selectedSort, sortOrder, splitGetBackordersCall);
};

async function action(input: GetBackorderListInput, ctx: Msdyn365.IActionContext): Promise<IGetBackorderListData> {
    // console.log(`input.selectedSort: ${input.selectedSort}`);
    // console.log(`input.sortOrder: ${input.sortOrder}`);
    // split getBackOrderList call into 3
    let totalItems = input.top;
    if (input.splitGetBackordersCall && totalItems > 3) {
        const numSplits = totalItems >= 15 ? 4 : 3;
        const numPerSplit = Math.ceil(totalItems / numSplits);
        const numPerSplitArray: number[] = [];
        const aggResponse: IGetBackOrderListResponse = {
            BackOrderLines: [],
            RecordCount: 0,
            AvailableSortOptions: [],
            SortOrder: '',
            SelectedSort: '',
            Top: 0
        };
        while (totalItems > numPerSplit) {
            numPerSplitArray.push(numPerSplit);
            totalItems -= numPerSplit;
        }
        if (totalItems > 0) {
            numPerSplitArray.push(totalItems);
        }
        const results = await Promise.all(
            numPerSplitArray.map((val, idx) => {
                return getBackOrderListAsync(
                    { callerContext: ctx },
                    val,
                    input.skip + numPerSplitArray.slice(0, idx).reduce((partialSum, a) => partialSum + a, 0),
                    input.selectedSort,
                    input.sortOrder
                );
            })
        );
        results.forEach(resp => {
            resp.BackOrderLines && aggResponse.BackOrderLines?.push(...resp.BackOrderLines);
            resp.RecordCount && (aggResponse.RecordCount = resp.RecordCount);
            resp.AvailableSortOptions && (aggResponse.AvailableSortOptions = resp.AvailableSortOptions);
            resp.SortOrder && (aggResponse.SortOrder = resp.SortOrder);
            resp.SelectedSort && (aggResponse.SelectedSort = resp.SelectedSort);
            if (resp.Top !== undefined && aggResponse.Top !== undefined && resp.Top >= 0 && aggResponse.Top >= 0) {
                aggResponse.Top += resp.Top;
            }
        });
        aggResponse.Skip = input.skip;
        return { backOrderData: aggResponse };
    } else {
        const response = await getBackOrderListAsync({ callerContext: ctx }, input.top, input.skip, input.selectedSort, input.sortOrder);
        return { backOrderData: response };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetBackorderListData>>action,
    id: 'GetBackorderList',
    input: createBackorderListInput
});
